import React, { useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { isAxiosError } from 'axios';
import { Check, ChevronDown, Languages, Loader2, Monitor, Moon, Sun } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { setPassword, patchCurrentUser } from '@/api/users';
import { telegramLogin, TelegramWidgetUser } from '@/api/auth';
import { TelegramLoginButton, isTelegramConfigured } from '@/components/Auth/TelegramLoginButton';
import { InstallPwaCard } from '@/components/InstallPwaCard';
import { cn } from '@/lib/utils';
import { usePageTitle } from '@/contexts/PageHeaderContext';

const PASSWORD_MIN = 8;

export default function AccountSettingsPage() {
  const { t, lang, setLang } = useT();
  const { preference, set: setTheme } = useTheme();
  const { user, isLoading, error: userError } = useCurrentUser();
  usePageTitle(t('account.title'), t('account.subtitle'));

  return (
    <div className="container mx-auto max-w-3xl space-y-4 px-4 py-6">
      <h1 className="sr-only">{t('account.title')}</h1>

      <Card variant="section">
        <CardHeader>
          <CardTitle>{t('account.profile')}</CardTitle>
          <CardDescription>{t('account.profileDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
            </div>
          )}
          {!!userError && !isLoading && !user && <ServerStatusNotice variant="card" error={userError} />}
          {user && (
            <dl className="grid gap-3 text-sm sm:grid-cols-[140px,1fr]">
              <dt className="text-muted-foreground">{t('account.username')}</dt>
              <dd className="font-medium">{user.username}</dd>
              <dt className="text-muted-foreground">{t('account.email')}</dt>
              <dd className="font-medium">{user.email || '—'}</dd>
              <dt className="text-muted-foreground">{t('account.role')}</dt>
              <dd className="font-medium">
                {user.is_superuser
                  ? t('account.roleSuperuser')
                  : user.is_staff
                    ? t('account.roleStaff')
                    : t('account.roleMember')}
              </dd>
            </dl>
          )}
        </CardContent>
      </Card>

      <ChangePasswordCard />

      <TelegramBindCard />

      <InstallPwaCard />

      <Card variant="section">
        <CardHeader>
          <CardTitle>{t('account.appearance')}</CardTitle>
          <CardDescription>{t('account.appearanceDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <section className="space-y-2">
            <div className="text-sm font-medium">{t('account.themeLabel')}</div>
            <div className="flex flex-wrap gap-2">
              <ChoiceButton
                active={preference === 'light'}
                onClick={() => setTheme('light')}
                icon={<Sun className="size-4" />}
                label={t('theme.lightShort')}
              />
              <ChoiceButton
                active={preference === 'dark'}
                onClick={() => setTheme('dark')}
                icon={<Moon className="size-4" />}
                label={t('theme.darkShort')}
              />
              <ChoiceButton
                active={preference === 'system'}
                onClick={() => setTheme('system')}
                icon={<Monitor className="size-4" />}
                label={t('theme.systemShort')}
              />
            </div>
          </section>

          {!user?.disable_language_picker && (
            <section className="space-y-2">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <Languages className="size-4 text-muted-foreground" />
                {t('account.languageLabel')}
              </div>
              <div className="flex flex-wrap gap-2">
                <ChoiceButton
                  active={lang === 'ru'}
                  onClick={() => { setLang('ru'); void patchCurrentUser({ lang: 'ru' }); }}
                  label={t('language.russian')}
                />
                <ChoiceButton
                  active={lang === 'en'}
                  onClick={() => { setLang('en'); void patchCurrentUser({ lang: 'en' }); }}
                  label={t('language.english')}
                />
              </div>
            </section>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ChoiceButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
        active
          ? 'border-primary bg-primary-muted text-primary-muted-foreground'
          : 'border-border bg-background text-foreground hover:bg-muted',
      )}
      aria-pressed={active}
    >
      {icon}
      <span>{label}</span>
      {active && <Check className="size-3.5" />}
    </button>
  );
}

function ChangePasswordCard() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  function reset() {
    setCurrent('');
    setNext('');
    setConfirm('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    if (next.length < PASSWORD_MIN) {
      setErrorMsg(t('account.passwordTooShort', { n: PASSWORD_MIN }));
      return;
    }
    if (next !== confirm) {
      setErrorMsg(t('account.passwordMismatch'));
      return;
    }
    if (next === current) {
      setErrorMsg(t('account.passwordSameAsCurrent'));
      return;
    }

    setSubmitting(true);
    try {
      await setPassword({ current_password: current, new_password: next });
      setSuccessMsg(t('account.passwordChanged'));
      reset();
    } catch (err) {
      setErrorMsg(extractDjoserError(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card variant="section">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-0 py-5 text-left md:px-5"
      >
        <CardTitle>{t('account.password')}</CardTitle>
        <ChevronDown
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
      <CardContent>
        <form onSubmit={handleSubmit} className="grid gap-4 sm:max-w-sm">
          {errorMsg && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}
          {successMsg && (
            <div className="rounded-md bg-success/10 px-3 py-2 text-sm text-success">
              {successMsg}
            </div>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="current-password">{t('account.passwordCurrent')}</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={e => setCurrent(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="new-password">{t('account.passwordNew')}</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={e => setNext(e.target.value)}
              required
              minLength={PASSWORD_MIN}
            />
            <p className="text-[11px] text-muted-foreground">
              {t('account.passwordMinHint', { n: PASSWORD_MIN })}
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="confirm-password">{t('account.passwordConfirm')}</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
              minLength={PASSWORD_MIN}
            />
          </div>

          <div>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('account.passwordSubmitting') : t('account.passwordSubmit')}
            </Button>
          </div>
        </form>
      </CardContent>
      )}
    </Card>
  );
}

function extractDjoserError(err: unknown, t: (k: string, p?: Record<string, string | number>) => string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data;
    if (data && typeof data === 'object') {
      const candidate =
        firstString((data as Record<string, unknown>).current_password) ||
        firstString((data as Record<string, unknown>).new_password) ||
        firstString((data as Record<string, unknown>).detail) ||
        firstString((data as Record<string, unknown>).non_field_errors);
      if (candidate) return candidate;
    }
    if (err.response?.status === 400) return t('account.passwordRejected');
  }
  return t('common.error');
}

function firstString(v: unknown): string | null {
  if (typeof v === 'string') return v;
  if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string') return v[0];
  return null;
}

function TelegramBindCard() {
  const { t } = useT();
  const { user } = useCurrentUser();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // No bot configured at build time → don't show dead UI.
  if (!isTelegramConfigured) return null;

  // Binding state lives on the BE — operator may have pre-bound the
  // username (confirmed=false; waiting for the user's first TG sign-
  // in to backfill telegram_id) or the user may have signed in via
  // TG previously (confirmed=true). Either way we render the bound
  // state; the user shouldn't see a "bind" button when their account
  // is already attached to a TG identity.
  const binding = user?.telegram_binding ?? null;
  const isBound = !!binding;

  // Widget-mode handler. In OIDC mode the button redirects to
  // oauth.telegram.org instead; the callback page handles binding
  // server-side and lands the user back here (with fresh /users/me/).
  const handleAuth = async (tgUser: TelegramWidgetUser) => {
    setErrorMsg(null);
    try {
      await telegramLogin(tgUser);
      // Hard reload so useCurrentUser refetches and the bound state
      // renders without a manual mutate.
      window.location.reload();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        setErrorMsg(t('account.telegramAlreadyTaken'));
      } else {
        setErrorMsg(t('account.telegramBindError'));
      }
    }
  };

  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle>{t('account.telegram')}</CardTitle>
        <CardDescription>{t('account.telegramDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isBound ? (
          <div className="flex items-center gap-2 text-sm">
            <Check className="size-4 text-success" />
            <span className="text-muted-foreground">{t('account.telegramBound')}</span>
            {binding?.telegram_username && (
              <span className="font-medium text-foreground">
                @{binding.telegram_username}
              </span>
            )}
            {binding && !binding.confirmed && (
              <span className="ml-1 text-[11px] text-muted-foreground">
                {t('account.telegramPendingFirstLogin')}
              </span>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{t('account.telegramBindPrompt')}</p>
            <TelegramLoginButton
              onAuth={handleAuth}
              label={t('account.telegramBindCta')}
              nextPath="/settings/account"
              compact
            />
          </>
        )}
        {errorMsg && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {errorMsg}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
